class SearchDrawer extends DrawerElement {
  constructor() {
    super();
  }

  get shouldAppendToBody() {
    return false;
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }

  get focusElement() {
    return this.querySelector('input[type="search"]');
  }
}
customElements.define('search-drawer', SearchDrawer);

class SearchTyped extends HTMLElement {
  constructor() {
    super();
    this._destroyed = false;
    this._slot = null;
    Motion.inView(this, this.init.bind(this));
  }

  get startDelay() {
    return this.hasAttribute('data-delay') ? parseFloat(this.getAttribute('data-delay')) : 0;
  }

  // Exact ICCONX mega menu categories
  get categories() {
    return [
      'True Wireless Earbuds',
      'Neckbands',
      'Wireless Speakers',
      'Soundbars',
      'Party Speakers',
      'Power Banks',
      'Chargers & Cables',
      'Wired Earphones',
      'New Launches'
    ];
  }

  async init() {
    // Fix: set a tight height so overflow:hidden clips just one line of text
    // This is the KEY fix — prevents text from showing outside
    this.style.overflow = 'hidden';
    this.style.height = '1.3em';
    this.style.maxWidth = '100%';

    // Create the inner span that slides vertically
    this._slot = document.createElement('span');
    this._slot.style.cssText = 'display:inline-block; will-change:transform; white-space:nowrap;';
    this.innerHTML = '';
    this.appendChild(this._slot);

    // NO external cursor — ICCONX.in style has no blinking cursor outside

    if (this.startDelay > 0) await this.sleep(this.startDelay * 1000);

    // ─── Infinite vertical slide — ICCONX.in style ───
    let i = 0;
    while (!this._destroyed) {
      await this.slideIn(this.categories[i % this.categories.length]);
      await this.sleep(2200);
      await this.slideOut();
      await this.sleep(80);
      i++;
    }
  }

  // Text slides in from BOTTOM → CENTER
  async slideIn(text) {
    if (this._destroyed) return;
    this._slot.textContent = text;
    // Set opacity to 0 first so Motion can animate from 0
    this._slot.style.opacity = '0';

    await Motion.animate(
      this._slot,
      { y: ['110%', '0%'], opacity: [0, 1] },
      { duration: 0.42, easing: [0.22, 1, 0.36, 1] }
    ).finished;
  }

  // Text exits CENTER → TOP
  async slideOut() {
    if (this._destroyed) return;

    await Motion.animate(
      this._slot,
      { y: ['0%', '-110%'], opacity: [1, 0] },
      { duration: 0.32, easing: [0.64, 0, 0.78, 0] }
    ).finished;
  }

  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  disconnectedCallback() {
    this._destroyed = true;
  }

  // Keep this method so theme code calling it doesn't error
  insertCursor() {}
}
customElements.define('search-typed', SearchTyped);

class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();

    this.cachedMap = new Map();
    this.focusElement = this.input;

    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('click', this.hideTypewriter.bind(this));
    this.input.addEventListener('input', theme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }

  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get typewriter() {
    return this.input.previousElementSibling;
  }

  hideTypewriter() {
    if (this.typewriter && !this.typewriter.hasAttribute('hidden')) {
      this.typewriter.setAttribute('hidden', '');
    }
  }

  onFocus(event) {
    if (this.closest('.collection')) {
      document.body.classList.add('predictive-search-open');

      const searchTerm = this.getQuery();
      if (searchTerm.length === 0) return;

      const url = this.buildUrl().toString();
      this.renderSection(url, event);
    }
  }

  clear(event = null) {
    if (event) event.preventDefault();

    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
  }

  getQuery() {
    return this.input.value.trim();
  }

  buildUrl() {
    const url = new URL(`${theme.routes.shop_url}${theme.routes.predictive_search_url}`);

    url.searchParams.set('q', this.getQuery());
    url.searchParams.set('resources[limit]', this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit')) : 3);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', theme.utils.sectionId(this));
    return url;
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }

    this.hideTypewriter();

    const url = this.buildUrl().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url)
      ? this.renderSectionFromCache(url)
      : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText);
    this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    this.setAttribute('loading', '');

    fetch(url, { signal: this.abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
        this.removeAttribute('loading');
        this.setAttribute('results', '');
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
    if (document.getElementById(id) === null) return;

    document.getElementById(id).innerHTML = new DOMParser()
      .parseFromString(responseText, 'text/html')
      .getElementById(id).innerHTML;
  }
}
customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

class PredictiveSearchOverlay extends OverlayElement {
  constructor() {
    super();
    this.addEventListener('click', this.onClick);
  }

  onClick() {
    setTimeout(() => {
      document.body.classList.remove('predictive-search-open');
    });
  }
}
customElements.define('predictive-search-overlay', PredictiveSearchOverlay);
